import { useState, useRef, useEffect } from "react";
import { Download, Image, FileText, Copy, Check } from "lucide-react";

export default function ExportMenu({ posts, aggregation }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const generateSummaryText = () => {
    const now = new Date();
    const etTime = now.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
    const avg = aggregation?.avg_score ?? 0;
    const total = aggregation?.total_posts ?? posts.length;

    let lines = [`MACROFLARE FEED EXPORT — ${etTime} ET`, `${total} posts analyzed · Avg score: ${avg >= 0 ? '+' : ''}${avg}`, ''];

    const topPosts = posts.slice(0, 20);
    for (const p of topPosts) {
      const score = p.analysis?.overall_score ?? 0;
      const handle = p.source?.handle || '';
      const text = p.content?.text || '';
      const instruments = (p.analysis?.instruments || []).map(i => `${i.symbol} ${i.score > 0 ? '+' : ''}${i.score}`).join(', ');
      lines.push(`[${score >= 0 ? '+' : ''}${score}] ${handle}: ${text.slice(0, 120)}`);
      if (instruments) lines.push(`  Instruments: ${instruments}`);
      lines.push('');
    }
    if (posts.length > 20) lines.push(`... and ${posts.length - 20} more posts`);
    lines.push('', 'Generated by MacroFlare · Not financial advice');
    return lines.join('\n');
  };

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(generateSummaryText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = generateSummaryText();
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleExportPNG = async () => {
    setOpen(false);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const feedEl = document.querySelector('[data-testid="feed-list-scroll-area"]');
      if (!feedEl) return;
      const canvas = await html2canvas(feedEl, {
        backgroundColor: '#0d1117',
        scale: 2,
        logging: false,
        useCORS: true,
      });
      const link = document.createElement('a');
      link.download = `macroflare-feed-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('PNG export failed:', err);
    }
  };

  const handleExportPDF = async () => {
    setOpen(false);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const { default: jsPDF } = await import('jspdf');
      const feedEl = document.querySelector('[data-testid="feed-list-scroll-area"]');
      if (!feedEl) return;
      const canvas = await html2canvas(feedEl, {
        backgroundColor: '#0d1117',
        scale: 2,
        logging: false,
        useCORS: true,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width / 2, canvas.height / 2],
      });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 2, canvas.height / 2);
      pdf.save(`macroflare-feed-${Date.now()}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
    }
  };

  const menuItems = [
    { icon: Copy, label: copied ? 'Copied!' : 'Copy Summary', action: handleCopyText, testId: 'export-copy' },
    { icon: Image, label: 'Export PNG', action: handleExportPNG, testId: 'export-png' },
    { icon: FileText, label: 'Export PDF', action: handleExportPDF, testId: 'export-pdf' },
  ];

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="export-menu-button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2 py-1 rounded font-mono text-[10px] tracking-[0.06em] uppercase transition-colors"
        style={{ color: '#8b949e', border: '1px solid #30363d' }}
        onMouseEnter={(e) => { e.currentTarget.style.color = '#c9d1d9'; e.currentTarget.style.borderColor = '#484f58'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = '#8b949e'; e.currentTarget.style.borderColor = '#30363d'; }}
      >
        <Download size={11} />
        Export
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-50 rounded-md border overflow-hidden min-w-[150px]"
          style={{ backgroundColor: '#1c2333', borderColor: '#30363d' }}
        >
          {menuItems.map(({ icon: Icon, label, action, testId }) => (
            <button
              key={testId}
              data-testid={testId}
              onClick={() => { action(); if (testId !== 'export-copy') setOpen(false); }}
              className="flex items-center gap-2 w-full text-left px-3 py-2 font-mono text-xs transition-colors"
              style={{ color: '#c9d1d9' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#21262d'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              {testId === 'export-copy' && copied ? <Check size={12} style={{ color: '#22c55e' }} /> : <Icon size={12} />}
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
